import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "../context/AuthContext";

const ModelContext = createContext();
export const useModels = () => useContext(ModelContext);

// Convert a base64 data-URL to a File object for FormData uploads
const base64ToFile = (dataUrl, filename = "image.jpg") => {
  const [meta, base64Data] = dataUrl.split(",");
  const mimeType = meta.match(/:(.*?);/)[1];
  const bytes = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
  return new File([bytes], filename, { type: mimeType });
};

const appendImageToForm = (form, image) => {
  if (!image || image.startsWith("/no-image")) return;
  if (image.startsWith("data:")) {
    form.append("image", base64ToFile(image));
  }
  // If it's already a URL (unchanged on edit), skip — backend keeps existing image
};

export const ModelProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const baseURL = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
  const imageURL = CDN_URL.endsWith("/") ? CDN_URL.slice(0, -1) : CDN_URL;
  const ENDPOINT = "/models";

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const safeSet = (updater) => { if (mountedRef.current) setModels(updater); };

  const authHeaders = () => ({
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  });

  const processModel = (m) => ({
    ...m,
    image: m.image ? `${imageURL}/${m.image}` : "/no-image.png",
    status: m.status ?? null,
  });

  const fetchModels = useCallback(async () => {
    if (!token) throw new Error("Missing auth token");
    setLoading(true);
    try {
      const res = await fetch(`${baseURL}${ENDPOINT}`, { headers: authHeaders() });
      const payload = await res.json().catch(() => null);

      if (!res.ok) throw new Error(payload?.message || `Fetch failed (${res.status})`);

      const items =
        Array.isArray(payload?.data) ? payload.data :
        Array.isArray(payload?.models) ? payload.models :
        Array.isArray(payload) ? payload :
        payload?.items ?? [];

      safeSet(items.map(processModel));
      return payload;
    } catch (err) {
      console.error("[Models] fetchModels:", err);
      if (err.message?.includes("Token Signature could not be verified")) {
        console.warn("[Auth] Invalid token — logging out");
        logout();
        return;
      }
      safeSet([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL]);

  const addModel = useCallback(async (modelData) => {
    if (!token) throw new Error("Missing auth token");

    const form = new FormData();
    form.append("category_id", modelData.categoryId);
    form.append("sub_category_id", modelData.subCategoryId);
    form.append("model_name", modelData.name);
    form.append("description", modelData.description);
    form.append("status", modelData.status);
    form.append("create_audit_id", modelData.create_audit_id || adminId);
    appendImageToForm(form, modelData.image);

    const res = await fetch(`${baseURL}${ENDPOINT}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });

    const payload = await res.json();
    if (!res.ok) throw new Error(payload?.message || `Add failed (${res.status})`);

    await fetchModels();
    return payload;
  }, [token, baseURL, adminId, fetchModels]);

  const updateModel = useCallback(async (id, modelData) => {
    if (!token) throw new Error("Missing auth token");

    const form = new FormData();
    if (modelData.categoryId)    form.append("category_id", modelData.categoryId);
    if (modelData.subCategoryId) form.append("sub_category_id", modelData.subCategoryId);
    if (modelData.name)          form.append("model_name", modelData.name);
    if (modelData.description)   form.append("description", modelData.description);
    if (modelData.status)        form.append("status", modelData.status);
    form.append("update_audit_id", modelData.update_audit_id || adminId);
    appendImageToForm(form, modelData.image);

    const res = await fetch(`${baseURL}${ENDPOINT}/${id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });

    const payload = await res.json();
    if (!res.ok) throw new Error(payload?.message || `Update failed (${res.status})`);

    const updated = processModel(payload.data || payload.model || payload);
    safeSet((prev) => prev.map((m) => m.id === id ? updated : m));
    return updated;
  }, [token, baseURL, adminId, imageURL]);

  const deleteModel = useCallback(async (id) => {
    if (!token) throw new Error("Missing auth token");

    const res = await fetch(`${baseURL}${ENDPOINT}/${id}`, {
      method: "DELETE",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ delete_audit_id: adminId }),
    });

    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload?.message || `Delete failed (${res.status})`);

    safeSet((prev) => prev.filter((m) => String(m.id) !== String(id)));
    return payload;
  }, [token, baseURL, adminId]);

  const toggleStatus = useCallback(async (id, currentStatus) => {
    if (!token) throw new Error("Missing auth token");

    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

    // Optimistic update
    safeSet((prev) => prev.map((m) => String(m.id) === String(id) ? { ...m, status: newStatus } : m));

    try {
      const res = await fetch(`${baseURL}${ENDPOINT}/${id}/status`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, update_audit_id: adminId }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || `Toggle failed (${res.status})`);
      return payload;
    } catch (err) {
      // Rollback on failure
      safeSet((prev) => prev.map((m) => String(m.id) === String(id) ? { ...m, status: currentStatus } : m));
      console.error("[Models] toggleStatus:", err);
      throw err;
    }
  }, [token, baseURL, adminId]);

  useEffect(() => {
    if (!isReady || !token) return;
    fetchModels().catch((err) => console.warn("[Models] initial fetch failed:", err.message));
  }, [isReady, token]);

  return (
    <ModelContext.Provider value={{ models, loading, fetchModels, addModel, updateModel, deleteModel, toggleStatus, setModels }}>
      {children}
    </ModelContext.Provider>
  );
};